/**
 * v3.15-1163 — Partner Benefits (growth outcomes, with proof).
 *
 * Every claim is backed by either a specific mechanism explanation
 * or a real Hub screenshot.
 */
import PublicLayout from '../components/PublicLayout';
import EndPageCTA from '../components/EndPageCTA';
import {
  HubCommunicationsScreenshot,
  HubDashboardScreenshot,
} from '../components/HubProductImagery';

const PartnerBenefitsPage = () => (
  <PublicLayout testId="partner-benefits-page">

    {/* HERO */}
    <section className="bg-primary text-white px-6 py-32 lg:py-44">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-8">Partner Benefits</p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-8">
          Built for partners who want<br />
          <span className="text-gradient-gold">to grow without growing overhead.</span>
        </h1>
        <p className="text-xl sm:text-2xl text-slate-300 font-light max-w-2xl leading-relaxed">
          Volume up. Headcount flat. Client experience elevated. That&rsquo;s the partnership.
        </p>
      </div>
    </section>

    {/* CAPACITY — with proof: Communications screenshot */}
    <section className="bg-white px-6 py-28 lg:py-36" data-testid="benefit-capacity">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Capacity</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.1] mb-6">
            You close more deals — without adding headcount.
          </h2>
          <p className="text-lg text-slate-600 max-w-xl leading-relaxed mb-4">
            Our Concierge team runs every file end-to-end. Documents, lender coordination, client communication, funding logistics — owned by us, visible to you.
          </p>
          <p className="text-sm text-slate-500 max-w-xl leading-relaxed">
            <span className="font-semibold text-slate-700">So what?</span> Your team stays selling. Volume goes up. Overhead doesn&rsquo;t.
          </p>
        </div>
        <HubCommunicationsScreenshot />
      </div>
    </section>

    {/* REACH — mechanism explanation, no screenshot */}
    <section className="bg-slate-50 px-6 py-28 lg:py-36" data-testid="benefit-reach">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Reach</p>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.1] max-w-4xl mb-6">
          You expand into business funding without learning a new business.
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl leading-relaxed mb-4">
          Working capital. Equipment. Expansion. Specialty commercial. We route, work, and fund under the same Concierge model your consumer files use.
        </p>
        <p className="text-sm text-slate-500 max-w-2xl leading-relaxed">
          <span className="font-semibold text-slate-700">So what?</span> A consumer client who mentions their business becomes a second deal — without your team learning new programs.
        </p>
      </div>
    </section>

    {/* RECOVERY — CreditForward360 mechanism */}
    <section className="bg-white px-6 py-28 lg:py-36" data-testid="benefit-recovery">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Recovery</p>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.1] max-w-4xl mb-6">
          You serve clients who would otherwise walk away.
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl leading-relaxed mb-4">
          CreditForward360 takes a not-yet client through a structured credit improvement program — diagnostic review, monitored plan, and automatic re-entry into the funding workflow when thresholds are met.
        </p>
        <p className="text-sm text-slate-500 max-w-2xl leading-relaxed">
          <span className="font-semibold text-slate-700">So what?</span> A declined client today becomes a funded client later. Same client. Same partner.
        </p>
      </div>
    </section>

    {/* SPEED — mechanism explanation */}
    <section className="bg-slate-50 px-6 py-28 lg:py-36" data-testid="benefit-speed">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Speed</p>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.1] max-w-4xl mb-6">
          You get paid the same day you fund.
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl leading-relaxed mb-4">
          ACH disbursement is the default. Same-day wire capability is available on qualified files. Both are coordinated by your Concierge and logged with full audit trails inside the Hub.
        </p>
        <p className="text-sm text-slate-500 max-w-2xl leading-relaxed">
          <span className="font-semibold text-slate-700">So what?</span> No bottlenecks at the finish line — funds move the day the lender confirms.
        </p>
      </div>
    </section>

    {/* CONFIDENCE — Dashboard proof */}
    <section className="bg-white px-6 py-28 lg:py-36" data-testid="benefit-confidence">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-16 items-center">
        <HubDashboardScreenshot />
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Confidence</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.1] mb-6">
            You never wonder where a file stands.
          </h2>
          <p className="text-lg text-slate-600 max-w-xl leading-relaxed mb-4">
            Every action your Concierge takes appears on your dashboard in real time. Lender response, document received, funding scheduled — all visible the moment it happens.
          </p>
          <p className="text-sm text-slate-500 max-w-xl leading-relaxed">
            <span className="font-semibold text-slate-700">So what?</span> No status calls. No follow-up emails. No black box.
          </p>
        </div>
      </div>
    </section>

    {/* Closing */}
    <section className="bg-gradient-to-b from-slate-900 to-primary text-white px-6 py-32 lg:py-44">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-8">
          Your business gets bigger.<br />
          <span className="text-gradient-gold">Your day gets quieter.</span>
        </h2>
      </div>
    </section>

    <EndPageCTA testId="benefits-end-cta" />
  </PublicLayout>
);

export default PartnerBenefitsPage;
